import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';
const SYMBOL_OPTIONS = [
    { value: 'USDT', label: 'USDT (Tether)' },
    { value: 'USDC', label: 'USDC' },
    { value: 'ETH', label: 'ETH' },
    { value: 'BTC', label: 'BTC' },
    { value: 'TRX', label: 'TRX' },
];
const NETWORK_OPTIONS = [
    { value: 'ERC20', label: 'ERC20 (Ethereum)' },
    { value: 'TRC20', label: 'TRC20 (Tron)' },
    { value: 'BEP20', label: 'BEP20 (BSC)' },
    { value: 'SOL', label: 'Solana' },
    { value: 'native', label: 'Native' },
];
const defaultFormData = {
    name: '',
    symbol: 'USDT',
    network: 'TRC20',
    address: '',
    qrCodeUrl: '',
    creditsPerUnit: '1',
    description: '',
    isActive: true,
};
export function CryptoWallets() {
    const { accessToken } = useAuthStore();
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingWallet, setEditingWallet] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const fetchWallets = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/crypto-wallets', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Failed to fetch crypto wallets');
            const data = await response.json();
            setWallets(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load crypto wallets');
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    useEffect(() => {
        fetchWallets();
    }, [fetchWallets]);
    const handleOpenModal = (wallet) => {
        if (wallet) {
            setEditingWallet(wallet);
            setFormData({
                name: wallet.name,
                symbol: wallet.symbol,
                network: wallet.network,
                address: wallet.address,
                qrCodeUrl: wallet.qrCodeUrl ?? '',
                creditsPerUnit: String(wallet.creditsPerUnit),
                description: wallet.description ?? '',
                isActive: wallet.isActive,
            });
        }
        else {
            setEditingWallet(null);
            setFormData(defaultFormData);
        }
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingWallet(null);
        setFormData(defaultFormData);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(editingWallet?.id ?? -1);
        try {
            const url = editingWallet
                ? `/api/admin/crypto-wallets/${editingWallet.id}`
                : '/api/admin/crypto-wallets';
            const method = editingWallet ? 'PUT' : 'POST';
            const body = JSON.stringify({
                name: formData.name,
                symbol: formData.symbol,
                network: formData.network,
                address: formData.address,
                qrCodeUrl: formData.qrCodeUrl || undefined,
                creditsPerUnit: parseFloat(formData.creditsPerUnit) || 1,
                description: formData.description || undefined,
                isActive: formData.isActive,
            });
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body,
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to save crypto wallet');
            }
            handleCloseModal();
            await fetchWallets();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleToggle = async (wallet) => {
        setActionLoading(wallet.id);
        try {
            const response = await fetch(`/api/admin/crypto-wallets/${wallet.id}/toggle`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Toggle failed');
            await fetchWallets();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to toggle');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDelete = async (wallet) => {
        if (!confirm(`"${wallet.name}" cüzdanını silmek istediğinize emin misiniz?`))
            return;
        setActionLoading(wallet.id);
        try {
            const response = await fetch(`/api/admin/crypto-wallets/${wallet.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Delete failed');
            await fetchWallets();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete');
        }
        finally {
            setActionLoading(null);
        }
    };
    const maskAddress = (addr) => {
        if (!addr || addr.length < 12)
            return '••••••••';
        return `${addr.slice(0, 8)}...${addr.slice(-8)}`;
    };
    if (loading)
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "Crypto Wallets" }), _jsx("p", { children: "Kredi y\u00FCkleme i\u00E7in kripto c\u00FCzdan adreslerini y\u00F6netin (USDT, vs.)" })] }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "+ Yeni C\u00FCzdan Ekle" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["Crypto Wallets (", wallets.length, ")"] }) }), wallets.length === 0 ? (_jsxs("div", { className: "admin-empty-state", children: [_jsx("p", { children: "Hen\u00FCz c\u00FCzdan eklenmemi\u015F." }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "\u0130lk C\u00FCzdan\u0131 Ekle" })] })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Ad" }), _jsx("th", { children: "Token" }), _jsx("th", { children: "A\u011F" }), _jsx("th", { children: "Adres" }), _jsx("th", { children: "Kredi/1 \u00DCnit" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "A\u00E7\u0131klama" }), _jsx("th", { children: "\u0130\u015Flemler" })] }) }), _jsx("tbody", { children: wallets.map((wallet) => (_jsxs("tr", { children: [_jsx("td", { children: wallet.name }), _jsx("td", { children: _jsx("span", { className: "provider-type-badge", children: wallet.symbol }) }), _jsx("td", { children: wallet.network }), _jsx("td", { children: _jsx("code", { className: "admin-code-inline", title: wallet.address, children: maskAddress(wallet.address) }) }), _jsx("td", { children: wallet.creditsPerUnit }), _jsx("td", { children: _jsx("button", { type: "button", className: `admin-toggle-btn ${wallet.isActive ? 'active' : ''}`, onClick: () => handleToggle(wallet), disabled: actionLoading === wallet.id, title: wallet.isActive ? 'Deaktif Et' : 'Aktif Et', children: wallet.isActive ? '✓ Aktif' : '✗ Pasif' }) }), _jsx("td", { children: wallet.description || '-' }), _jsx("td", { children: _jsxs("div", { className: "admin-action-buttons", children: [_jsx("button", { className: "admin-edit-btn", onClick: () => handleOpenModal(wallet), disabled: actionLoading === wallet.id, title: "D\u00FCzenle", children: "\u270F\uFE0F" }), _jsx("button", { className: "admin-delete-btn", onClick: () => handleDelete(wallet), disabled: actionLoading === wallet.id, title: "Sil", children: actionLoading === wallet.id ? '...' : '🗑' })] }) })] }, wallet.id))) })] }) }))] }), showModal && (_jsx("div", { className: "admin-modal-overlay", onClick: handleCloseModal, children: _jsxs("div", { className: "admin-modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "admin-modal-header", children: [_jsx("h2", { children: editingWallet ? 'Cüzdan Düzenle' : 'Yeni Cüzdan Ekle' }), _jsx("button", { className: "admin-modal-close", onClick: handleCloseModal, children: "\u2715" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "admin-form", children: [_jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Ad *", _jsx("input", { type: "text", className: "admin-form-input", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), required: true, placeholder: "\u00D6rn: Tether TRC20 Ana" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Token", _jsx("select", { className: "admin-form-select", value: formData.symbol, onChange: (e) => setFormData({ ...formData, symbol: e.target.value }), children: SYMBOL_OPTIONS.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))) })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["A\u011F", _jsx("select", { className: "admin-form-select", value: formData.network, onChange: (e) => setFormData({ ...formData, network: e.target.value }), children: NETWORK_OPTIONS.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))) })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["C\u00FCzdan Adresi *", _jsx("input", { type: "text", className: "admin-form-input", value: formData.address, onChange: (e) => setFormData({ ...formData, address: e.target.value }), required: true, placeholder: "T... veya 0x..." })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["QR Kod URL (opsiyonel)", _jsx("input", { type: "text", className: "admin-form-input", value: formData.qrCodeUrl, onChange: (e) => setFormData({ ...formData, qrCodeUrl: e.target.value }), placeholder: "https://..." })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Kredi / 1 \u00DCnit (1 USDT = X kredi)", _jsx("input", { type: "number", step: "0.01", min: "0.01", className: "admin-form-input", value: formData.creditsPerUnit, onChange: (e) => setFormData({ ...formData, creditsPerUnit: e.target.value }), placeholder: "1" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["A\u00E7\u0131klama", _jsx("textarea", { className: "admin-form-textarea", value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), placeholder: "\u00D6rn: Sadece USDT TRC20 i\u00E7in", rows: 2 })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-setting-checkbox", children: [_jsx("input", { type: "checkbox", checked: formData.isActive, onChange: (e) => setFormData({ ...formData, isActive: e.target.checked }) }), _jsx("span", { children: "Aktif (Kredilerde g\u00F6ster)" })] }) }), _jsxs("div", { className: "admin-form-actions", children: [_jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", onClick: handleCloseModal, disabled: actionLoading !== null, children: "\u0130ptal" }), _jsx("button", { type: "submit", className: "admin-btn admin-btn-primary", disabled: actionLoading !== null, children: actionLoading !== null ? 'Kaydediliyor...' : editingWallet ? 'Güncelle' : 'Kaydet' })] })] })] }) }))] }));
}
